import { useCallback, useMemo, useState } from "react";
import {
  League,
  LeagueListElement,
  LeagueListMinimum,
  LanguageLocaleEnum,
  Squad,
  SquadList as ApiSquadList
} from "../../../client/api";
import { translatePlayerPosition } from "../../../config/utils";
import { PLAYER_POSITION_SORT_DIRECTION } from "../../../config/constants";

export type SquadSortField = "position" | "given" | "family" | "birth";
export type SquadSortDir = "asc" | "desc";

export function useSquadListController(
  squad: ApiSquadList | undefined,
  leagueForPosition: League | LeagueListElement | LeagueListMinimum | boolean | undefined,
  l: LanguageLocaleEnum
) {
  const [sortDir, setSortDir] = useState<SquadSortDir>("asc");
  const [sortField, setSortField] = useState<SquadSortField>("position");
  const [q, setQ] = useState("");

  const sorter = useCallback(
    (a: Squad, b: Squad) => {
      const direction = sortDir === "asc" ? 1 : -1;

      const fullName = (s: Squad) =>
        `${s.player.givenName} ${s.player.familyName}`;
      const fullNameFamilyFirst = (s: Squad) =>
        `${s.player.familyName} ${s.player.givenName}`;

      const compareStrings = (sa: string, sb: string) =>
        sa.localeCompare(sb) * direction;

      switch (sortField) {
        case "family":
          return compareStrings(
            fullNameFamilyFirst(a),
            fullNameFamilyFirst(b)
          );

        case "given":
          return compareStrings(fullName(a), fullName(b));

        case "birth": {
          const timeA = a.player.birthdate
            ? new Date(a.player.birthdate).getTime()
            : Number.NEGATIVE_INFINITY;
          const timeB = b.player.birthdate
            ? new Date(b.player.birthdate).getTime()
            : Number.NEGATIVE_INFINITY;

          if (timeA !== timeB) {
            return (timeA - timeB) * direction;
          }
          return compareStrings(fullName(a), fullName(b));
        }

        case "position":
        default: {
          const idxA = PLAYER_POSITION_SORT_DIRECTION.indexOf(
            a.player.playerPosition.key
          );
          const idxB = PLAYER_POSITION_SORT_DIRECTION.indexOf(
            b.player.playerPosition.key
          );

          const baseIdxA =
            idxA === -1 ? PLAYER_POSITION_SORT_DIRECTION.length : idxA;
          const baseIdxB =
            idxB === -1 ? PLAYER_POSITION_SORT_DIRECTION.length : idxB;

          const aNum = baseIdxA * 10;
          const bNum = baseIdxB * 10;

          const nameComp = fullNameFamilyFirst(a).localeCompare(
            fullNameFamilyFirst(b)
          );

          return nameComp + (aNum - bNum);
        }
      }
    },
    [sortDir, sortField]
  );

  const filteredSquad = useMemo(() => {
    if (!squad) return [] as ApiSquadList;

    const buildSearchString = (s: Squad) => {
      const positionLabel = translatePlayerPosition(
        s.player.playerPosition.key,
        leagueForPosition,
        l
      );
      return `${s.player.givenName} ${s.player.familyName} ${positionLabel}`.toLowerCase();
    };

    if (q.length === 0) {
      return [...squad].sort(sorter) as ApiSquadList;
    }

    const queryTokens = q.toLowerCase().split(" ").filter(Boolean);

    return squad
      .filter((s) => {
        const haystack = buildSearchString(s);
        return queryTokens.some((token) => haystack.includes(token));
      })
      .sort(sorter) as ApiSquadList;
  }, [squad, leagueForPosition, l, q, sorter]);

  const onSortBirth = useCallback(() => {
    if (sortField === "birth") {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
      return;
    }
    if (sortDir === "desc") {
      setSortDir("asc");
    }
    setSortField("birth");
  }, [sortDir, sortField]);

  const onSortGiven = useCallback(() => {
    if (sortField === "given") {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
      return;
    }
    if (sortDir === "desc") {
      setSortDir("asc");
    }
    setSortField("given");
  }, [sortDir, sortField]);

  const onSortFamily = useCallback(() => {
    if (sortField === "family") {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
      return;
    }
    if (sortDir === "desc") {
      setSortDir("asc");
    }
    setSortField("family");
  }, [sortDir, sortField]);

  const onSortPosition = useCallback(() => {
    if (sortField === "position") {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
      return;
    }
    if (sortDir === "desc") {
      setSortDir("asc");
    }
    setSortField("position");
  }, [sortDir, sortField]);

  return {
    sortDir,
    sortField,
    q,
    setQ,
    filteredSquad,
    onSortBirth,
    onSortGiven,
    onSortFamily,
    onSortPosition
  };
}



